import React from "react";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import Heading from "components/Heading";
import Input from "components/Input";
import SimpleSelect from "components/SimpleSelect";
import { useNavigate } from "react-router";
import { organisationStore } from "features/Organisation/OrganisationStore";
import { observer } from "mobx-react-lite";
import TagsInput, { TagsOption } from "components/TagsInput";
import { ROUTES_ENUM } from "routes/consts";
import ImageUploader from "components/ImageUploader";
import { organisationTypesAvailable } from "../CreateOrganisation.tsx";

interface OrganisationInfo {
  name?: string;
  type?: string;
  tags?: string[];
  image?: File[] | null;
  domains: TagsOption[];
}

const CreateOrganisation = ({ close }: { close?: () => void }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [showMandatoryAlert, setShowMandatoryAlert] = React.useState(false);
  const [organisationInfo, setOrganisationInfo] =
    React.useState<OrganisationInfo>({ name: "", type: "", domains: [] });

  function addDomain(domain: string) {
    setOrganisationInfo((state) => ({
      ...state,
      domains: [...state.domains, { id: domain, name: domain }],
    }));
  }

  function removeDomain(domain: string) {
    setOrganisationInfo((state) => ({
      ...state,
      domains: state.domains.filter((d) => d.name !== domain),
    }));
  }

  const handleFieldChange = <K extends keyof OrganisationInfo>(
    field: K,
    value: OrganisationInfo[K]
  ) => {
    setOrganisationInfo((prev) => ({ ...prev, [field]: value }));
  };

  async function submitForm() {
    const organisationName = String(organisationInfo.name);
    const organisationTypeId = String(organisationInfo.type);
    const organisationLogo = organisationInfo.image?.[0] as File | null;
    const organisationDomains = organisationInfo.domains.map((d) => d.name);
    if (!organisationName || organisationName === "") {
      setShowMandatoryAlert(true);
      return;
    }
    if (!organisationTypeId || organisationTypeId === "") {
      setShowMandatoryAlert(true);
      return;
    }
    await organisationStore
      .createOrganisation(
        organisationName,
        organisationTypeId,
        organisationDomains,
        organisationLogo
      )
      .then(() => {
        navigate(`/${ROUTES_ENUM.DASHBOARD}`);
        close && close();
      });
  }

  const organisationTypes = organisationTypesAvailable(
    organisationStore.organisationTypes,
    window.location.href
  );

  return (
    <div className="md:max-w-[720px] pb-6 m-auto lg:h-auto md:h-[calc(100vh-120px)] flex flex-col justify-center">
      <Heading size="title" className="mb-6">
        {t("onboarding.createOrganisation")}
      </Heading>
      <form className="text-left">
        <ImageUploader
          labelName="Logo"
          name="image"
          onChange={(image) => handleFieldChange("image", image)}
          buttonText={t("onboarding.selectFile")}
        />
        <div className="flex gap-4 w-full justify-between">
          <Input
            name="name"
            labelName={t("onboarding.name")}
            placeholder={t("onboarding.organisationNamePlaceholder")}
            className="mb-1"
            containerClassName="w-full"
            value={organisationInfo.name}
            onChange={(name) => handleFieldChange("name", name)}
            required
            showMandatoryAlert={showMandatoryAlert}
            errorMessage={t("onboarding.nameErrorMsg")}
          />
          {organisationTypes?.length > 0 && (
            <SimpleSelect
              labelName={t("onboarding.organisationType")}
              name="type"
              containerClassName="w-full"
              options={organisationTypes}
              placeholder={t("onboarding.organisationTypePlaceholder")}
              required
              onChange={(type) => handleFieldChange("type", type)}
              current={organisationInfo.type}
              showMandatoryAlert={showMandatoryAlert}
              errorMessage={t("onboarding.organisationTypeErrorMsg")}
            />
          )}
        </div>
        <div className="mb-8">
          <div className="text-sm text-gray-700">
            {t("onboarding.domainOfCompany")}
          </div>
          <TagsInput
            items={organisationInfo.domains}
            description={t("onboarding.domainOfCompanySubtitle")}
            addItem={addDomain}
            removeItem={removeDomain}
            regEx={new RegExp("^@[a-zA-Z0-9.-]+$")}
            placeholder={t("onboarding.domainPlaceholder")}
          />
        </div>
        <div className="flex justify-between">
          <Button type="gray" onClick={close} width="fit-content">
            {t("commons.back")}
          </Button>
          <Button
            type="primary"
            size="default"
            onClick={submitForm}
            width="fit-content"
          >
            {t("onboarding.createOrganisation")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default observer(CreateOrganisation);
